import { useMemo, useRef } from "react";
import { api } from "../api";
import { CountryInput } from "../components/CountryInput";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { PuzzleMap } from "../components/puzzle/PuzzleMap";
import { useGame } from "../useGame";

interface C {
  code: string;
  name: string;
}
interface View {
  from: C;
  to: C;
  par: number;
  path: C[];
  misses: (C & { why: "far" | "used" })[];
  guesses: number;
  answer?: C[];
}

const COPY = { unknown_country: "Не знаю такої країни", already_guessed: "Вже в маршруті" };
/** The route line and the glowing chain get their moment before the sheet. */
const OPTS = { sheetDelayMs: 2200 };

function Flag({ code, size = "h-5 w-7" }: { code: string; size?: string }) {
  return <img src={`/flags/${code.toLowerCase()}.svg`} alt="" className={`${size} rounded-[3px] object-cover`} />;
}

export function Travle({ date }: { date?: string | undefined }) {
  const g = useGame<View>("travle", COPY, date, OPTS);
  const restoredMisses = useRef<number | null>(null);
  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const used = useMemo(
    () => new Set([state?.view.from.name, ...(state?.view.path.map((c) => c.name) ?? [])].filter(Boolean) as string[]),
    [state?.view.path, state?.view.from.name],
  );

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const lastMiss = v.misses[v.misses.length - 1];
  if (restoredMisses.current === null) restoredMisses.current = v.misses.length;

  return (
    <Shell header={<Header left={`#${s.number}`} title="TRAVLE" right={`🔥 ${s.me.streak}`} />}>
      {/* Route header: A → … → B */}
      <div className="px-4 pb-2">
        <div className="flex items-center justify-between rounded-xl bg-tgsection px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <Flag code={v.from.code} />
            <span className="truncate text-sm font-semibold">{v.from.name}</span>
          </div>
          <span className="px-2 text-tghint">→</span>
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-semibold">{v.to.name}</span>
            <Flag code={v.to.code} />
          </div>
        </div>
        <div className="mt-1 flex justify-between px-1 text-[11px] text-tghint">
          <span>між ними мінімум: {v.par} {v.par === 1 ? "країна" : v.par < 5 ? "країни" : "країн"}</span>
          <span>спроб: {v.guesses}</span>
        </div>
      </div>

      <CountryInput
        disabled={finished}
        busy={g.busy}
        exclude={used}
        placeholder="країна, що межує з маршрутом"
        onSubmit={(name) => void g.run(() => api.guess<View>("travle", name, date))}
      />

      <div className="px-4 pb-2">
        <PuzzleMap
          from={v.from.code}
          to={v.to.code}
          path={v.path.map((c) => c.code)}
          misses={v.misses.map((m) => m.code)}
          answer={v.answer?.map((c) => c.code) ?? null}
          status={s.status}
        />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-2">
        {/* Chain so far */}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Chip c={v.from} tone="start" />
          {v.path.map((c, i) => (
            <span key={c.code} className="flex items-center gap-1.5">
              <span className="text-tghint">→</span>
              <Chip c={c} tone={i === v.path.length - 1 && !finished ? "current" : "ok"} />
            </span>
          ))}
          {!finished && (
            <span className="flex items-center gap-1.5">
              <span className="text-tghint">→</span>
              <span className="rounded-full border border-dashed border-[rgba(128,128,128,0.5)] px-2.5 py-1 text-xs text-tghint">?</span>
            </span>
          )}
          {finished && s.status === "won" ? null : (
            <span className="flex items-center gap-1.5">
              <span className="text-tghint">…</span>
              <Chip c={v.to} tone="goal" />
            </span>
          )}
        </div>

        {lastMiss && !finished && v.misses.length > (restoredMisses.current ?? 0) && (
          <div className="mt-3 text-sm text-tghint">
            <b className="text-tgtext">{lastMiss.name}</b> не межує з жодним шматочком маршруту
          </div>
        )}

        {v.misses.length > 0 && (
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-wide text-tghint">не підійшли ({v.misses.length})</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {v.misses.map((m, i) => (
                <span
                  key={`${m.code}-${i}`}
                  className={`flex items-center gap-1 rounded-md bg-cold/15 px-2 py-0.5 text-xs ${
                    i >= (restoredMisses.current ?? 0) ? "pz-tray-new" : ""
                  }`}
                >
                  <Flag code={m.code} size="h-3.5 w-5" /> {m.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {finished && v.answer && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-wide text-tghint">найкоротший маршрут</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {v.answer.map((c, i) => (
                <span key={c.code} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-tghint">→</span>}
                  <Chip c={c} tone="ok" />
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <div className="flex flex-1 items-center text-xs text-tghint">кроків: {v.path.length} · промахів: {v.misses.length}</div>
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("travle", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={s.status === "won" ? "Доїхав" : "Здався"}
          subtitle={
            s.status === "won"
              ? `${v.path.length} кроків, ${v.misses.length} промахів (найкоротше ${v.par}) · стрік ${s.me.streak} 🔥`
              : `стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}

function Chip({ c, tone }: { c: C; tone: "start" | "ok" | "current" | "goal" }) {
  const cls =
    tone === "current"
      ? "bg-tgbutton text-tgbuttontext"
      : tone === "goal"
        ? "border border-[rgba(128,128,128,0.5)]"
        : tone === "start"
          ? "bg-[rgba(128,128,128,0.25)]"
          : "bg-ok/25";
  return (
    <span className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>
      <Flag code={c.code} size="h-3.5 w-5" /> {c.name}
    </span>
  );
}
